/**
 * Toast / undo controller — §12.4.
 *
 * "Dismiss is optimistic and undoable for 5 s."
 *
 * The timer lives in the controller rather than the component so a dismiss
 * survives navigating away from the card that triggered it.
 */

import { createSignal, onCleanup, type Accessor } from 'solid-js';

export interface Toast {
  id: number;
  message: string;
  /** Present when the action is reversible; drives the Undo button. */
  undo?: () => void | Promise<void>;
  tone: 'neutral' | 'error';
}

export const UNDO_WINDOW_MS = 5_000;

export interface ToastController {
  signals: { toast: Accessor<Toast | null> };
  derivations: { visible: Accessor<boolean> };
  actions: {
    show: (message: string, undo?: () => void | Promise<void>) => void;
    error: (message: string) => void;
    dismiss: () => void;
    runUndo: () => void;
  };
}

export function createToastController(): ToastController {
  const [toast, setToast] = createSignal<Toast | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let nextId = 1;

  const clearTimer = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  const push = (message: string, tone: Toast['tone'], undo?: () => void | Promise<void>): void => {
    clearTimer();
    setToast({ id: nextId++, message, tone, ...(undo ? { undo } : {}) });
    timer = setTimeout(() => setToast(null), UNDO_WINDOW_MS);
  };

  onCleanup(clearTimer);

  return {
    signals: { toast },
    derivations: { visible: () => toast() !== null },
    actions: {
      show: (message, undo) => push(message, 'neutral', undo),
      error: (message) => push(message, 'error'),
      dismiss: () => {
        clearTimer();
        setToast(null);
      },
      runUndo: () => {
        const current = toast();
        clearTimer();
        setToast(null);
        void current?.undo?.();
      },
    },
  };
}
